"use client";
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useState } from "react";

export default function PlatformPage() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const partnerTypes = [
  {
    href: '/partners/professional-services',
    title: 'Professional Services',
    desc: 'We collaborate with architects, structural engineers, MEP engineers, and consultants trained in the NeXSYS system to deliver rapid, high-quality project outcomes.'
  },
  {
    href: '/partners/channel',
    title: 'Channel Partners',
    desc: 'Become a licensed user, wholesaler, producer, or manufacturer authorised to use, distribute, or produce NeXSYS systems in your market.'
  },
  {
    href: '/partners/supply-chain',
    title: 'Supply Chain Partners',
    desc: 'Material suppliers, MEP equipment providers, and logistics partners who integrate seamlessly into the NeXSYS manufacturing and delivery ecosystem.'
  },
  {
    href: '/partners/installers-setters',
    title: 'Installers & Setters',
    desc: 'Certified site installers and finishers trained to assemble, install, and complete NeXSYS buildings to the highest quality and safety standards.'
  }];


  const stakeholders = [
  {
    title: 'For Developers',
    items: [
    'Unified Structured Process from Design to Hand over',
    'Faster Design & Engineering Phase',
    'Predictable costs with standardized systems',
    'Decreasing RISKs and Increase in Quality']

  },
  {
    title: 'For Supply Chain',
    items: [
    'True Integration into manufacturing process and final product',
    'Streamlining of procurement',
    'Systematic Material Approval & Specifications',
    'Penetration into New Segment through Partnership']

  },
  {
    title: 'For Manufacturers',
    items: [
    'Reduction of Fabrication cost',
    'Production capability licensing',
    'Standardized component specifications',
    'Quality assurance protocols while allowing for High-volume production']

  },
  {
    title: 'For Contractors',
    items: [
    'Ease of onsite Installation by Contractor',
    'Access to Supply chain and Installers',
    'Predictable project timelines',
    'Lower construction risk, skill labor and increase predictability']

  }];


  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative min-h-[40vh] flex items-end pb-16 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/assets/images/ChatGPT_Image_Dec_29_2025_12_31_58_PM-1774454582787.png"
              alt="NeXSYS Platform Interface"
              className="w-full h-full object-cover object-top" />
            
            <div className="absolute inset-0 bg-gray-500/50" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, hsla(215, 28%, 10%, 0.7) 0%, hsla(207, 52%, 25%, 0.8) 100%)' }} />
          </div>
          <div className="relative container mx-auto container-padding pt-20">
            <div className="max-w-3xl">
              <h1 className="text-hero lg:text-hero-lg font-heading font-bold text-white mb-6">
                The <span className="font-bold text-white">NeXSYS</span> Platform
              </h1>
              <p className="text-xl text-white/80 leading-relaxed">
                From framing system to integrated construction platform - a revolutionary approach that transforms how buildings are designed, manufactured, and assembled.
              </p>
            </div>
          </div>
        </section>

        {/* Platform Description */}
        <section className="section-padding bg-card">
          <div className="container mx-auto container-padding">
            <h2 className="text-section lg:text-section-lg font-heading font-bold text-foreground mb-8">
              A Unified Platform for Industrialization Construction
            </h2>
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div className="space-y-6 text-muted-foreground text-lg leading-relaxed">
                <p>
                  The <span className="font-bold text-nexsys-brand">NeXSYS</span> Platform is a unified construction ecosystem built around the <span className="font-bold text-nexsys-brand">NeXSYS</span> Frame System - a precision-engineered, standardized node-and-beam structure that forms the primary load-bearing framework of the building. Designed as a common structural language, the frame enables seamless integration with MEP systems, façades, flooring, roofing, fenestration, fire and life-safety systems, and interior finishes while maintaining architectural flexibility, speed, and dimensional certainty.
                </p>
                <p>
                  The platform is powered by <span className="font-bold text-nexsys-brand">NeXSYS</span>&apos;s internally developed Design-for-Manufacturing (DFM) software, which connects architectural and engineering design directly to production and assembly. Manufacturing logic, tolerances, sequencing, and code compliance are embedded from the outset, enabling factory-ready outputs and reducing coordination risk between design intent and execution.
                </p>
                <p>
                  Supporting the system is a globally aligned supply chain of approved partners whose products are validated for compatibility with the <span className="font-bold text-nexsys-brand">NeXSYS</span> Frame System. Fabrication and assembly are delivered through certified manufacturers and installation partners, ensuring consistent quality and repeatable outcomes across regions. Projects are designed and coordinated by approved architects, engineers, and consultants trained to operate within the <span className="font-bold text-nexsys-brand">NeXSYS</span> ecosystem, ensuring alignment from concept through delivery.
                </p>
                <p>
                  Together, these elements form a scalable, governed platform that transforms construction from a fragmented process into an integrated, industrialized system.
                </p>
              </div>
              <div className="flex flex-col items-center">
                <img
                  src="/assets/images/platform-diagram-1774074241312.png"
                  alt="NeXSYS Platform Concept Diagram"
                  className="w-full rounded-2xl cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => setLightboxOpen(true)} />

                <p className="mt-4 text-sm text-muted-foreground text-center">
<span className="font-bold text-nexsys-brand">NeXSYS</span> Platform Concept
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits for Stakeholders */}
        <section className="section-padding">
          <div className="container mx-auto container-padding">
            <div className="mb-12 lg:mb-16 text-center">
              <h2 className="text-section lg:text-section-lg font-heading font-bold text-foreground mb-4">
                Benefits for All Stakeholders
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                The <span className="font-bold text-nexsys-brand">NeXSYS</span> platform creates value across the entire construction ecosystem.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {stakeholders?.map((s) =>
              <div key={s?.title} className="bg-card rounded-xl p-6 shadow-card border border-border">
                  <h3 className="font-heading font-bold text-lg text-foreground mb-4">{s?.title}</h3>
                  <ul className="space-y-3">
                    {s?.items?.map((item) =>
                  <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <img
                      src="https://www.nexsys.group/assets/bullet-icon-DqSm52JP.png"
                      alt=""
                      className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    
                        {item}
                      </li>
                  )}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Join the Platform */}
        <section className="section-padding bg-primary/10">
          <div className="container mx-auto container-padding">
            <div className="mb-12 lg:mb-16 text-center">
              <h2 className="text-section lg:text-section-lg font-heading font-bold text-foreground mb-4">
                Join the Platform
              </h2>
              <p className="text-lg text-muted-foreground">
                Partner with NeXSYS to revolutionize the construction industry.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {partnerTypes?.map((p) =>
              <Link
                key={p?.title}
                href={p?.href}
                className="group bg-card rounded-xl p-6 shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 border border-border block">
                
                  <h3 className="font-heading font-bold text-lg text-foreground mb-3">{p?.title}</h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    {p?.desc?.split('NeXSYS')?.map((part, i, arr) => (
                      <React.Fragment key={i}>
                        {part}
                        {i < arr?.length - 1 && <span className="font-bold text-nexsys-brand">NeXSYS</span>}
                      </React.Fragment>
                    ))}
                  </p>
                  <span className="inline-flex items-center gap-1 text-primary text-sm font-medium group-hover:gap-2 transition-all">
                    Learn more
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14" />
                      <path d="m12 5 7 7-7 7" />
                    </svg>
                  </span>
                </Link>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      {/* Lightbox Modal */}
      {lightboxOpen &&
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/75"
        onClick={() => setLightboxOpen(false)}>
        
          <div
          className="relative"
          style={{ width: "80vw", maxHeight: "80vh" }}
          onClick={(e) => e?.stopPropagation()}>
          
            <button
            onClick={() => setLightboxOpen(false)}
            className="absolute -top-10 right-0 text-white text-3xl font-bold leading-none hover:text-gray-300 transition-colors"
            aria-label="Close lightbox">
            
              &times;
            </button>
            <img
            src="/assets/images/platform-diagram-1774074241312.png"
            alt="NeXSYS Platform Concept Diagram enlarged"
            className="w-full h-full object-contain rounded-xl"
            style={{ maxHeight: "80vh" }} />
          
          </div>
        </div>
      }
    </div>
  );


}